import { useCallback, useEffect, useRef, useState } from 'react'

// Types
import {
  ImplantReportStatus,
  ImplantReportType,
  TUploadFileObject,
} from 'common/types'
import { fileToBase64WithThumbnail } from 'lib/utils/fileToBase64'

// Utils
import { pdfToBase64Image } from 'common/utils'
import { enqueueSnackbar } from 'notistack'

// Types
import { ImplantReportSenderModalLogicProps } from '../ImplantReportSender.types'
import { DynamicFormValues } from '../ReportsDynamicForm/types'

// Services
import { useSendAndPreviewSurgeryReport } from 'lib/services/api/email-service/sendAndPreviewSurgeryReport'
import { useGetOrganizationReports } from 'lib/services/api/email-service/getOrganizationReports.ts'

export const useImplantReportSenderModalLogic = ({
  surgeryId,
  implantReportStatus,
  implantReportInputs,
  unformatEnumValue,
  setInputModalOpen,
}: ImplantReportSenderModalLogicProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const {
    mutateAsync: sendAndPreviewSurgeryReportMutation,
    isLoading: isLoadingSendAndPreview,
  } = useSendAndPreviewSurgeryReport()
  const { data: organizationReports } = useGetOrganizationReports(surgeryId)

  const template = organizationReports?.reports?.find(
    (report) => report.id === implantReportInputs?.['reportType']?.id
  )

  // States
  const [reportAlreadySentModal, setReportAlreadySentModal] =
    useState<boolean>(false)
  const [previewModalOpen, setPreviewModalOpen] = useState<boolean>(false)
  const [previewPdfUrl, setPreviewPdfUrl] = useState<string>('')
  const [attachments, setAttachments] = useState<TUploadFileObject[]>([])

  // Constants
  const reportAlreadySent = (option: keyof typeof ImplantReportType) => {
    const enumType = unformatEnumValue(option)
    return (implantReportStatus as ImplantReportStatus[]).some(
      (status) => status.type === enumType && status.isSent
    )
  }

  // solves async state update on submitOverride flag issue
  useEffect(() => {
    if (implantReportInputs.submitOverride) {
      handleFormSubmit(implantReportInputs.reportInputs)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [implantReportInputs.submitOverride])

  const handleOnClose = () => {
    setInputModalOpen(false)
  }

  const handleAttach = useCallback(() => {
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
      fileInputRef.current.click()
    }
  }, [])

  const handleAttachementFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const selectedFiles = event.target.files || null

    if (!selectedFiles || selectedFiles.length === 0) {
      console.error('No file selected.')
      return
    }

    try {
      const newAttachments = await Promise.all(
        Array.from(selectedFiles).map(async (file: File) => {
          const { base64, thumbnail } = await fileToBase64WithThumbnail(file)
          if (base64 && thumbnail) {
            return {
              filename: file.name,
              mimetype: file.type,
              encoding: 'base64',
              content: base64,
              size: file.size,
              thumbnail:
                file.name.split('.').pop()?.toLocaleLowerCase() === 'pdf'
                  ? await pdfToBase64Image(base64)
                  : thumbnail,
            }
          }
        })
      )

      setAttachments((prev) => [
        ...prev,
        ...newAttachments.filter(
          (attachment): attachment is TUploadFileObject =>
            attachment !== undefined
        ),
      ])
    } catch (error) {
      console.error('Error processing files:', error)
    }
  }

  const handleRemoveAttachment = (index: number) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index))
  }

  const submitOrPreviewReport = async (
    values: DynamicFormValues,
    isPreview: boolean = false
  ) => {
    try {
      if (
        attachments.reduce((acc, curr) => acc + curr.size, 0) >=
        4 * 1024 * 1024
      ) {
        enqueueSnackbar('Attachments size exceeds 4MB', {
          variant: 'error',
        })
        setReportAlreadySentModal(false)
        return
      }

      // Map attachments to correct format
      const mappedAttachments = attachments.map((attachment) => ({
        filename: attachment.filename,
        contentType: attachment.mimetype,
        encoding: attachment.encoding,
        content: attachment.content,
      }))

      // Get physicianId from the form values (now handled by dynamic form)
      const physicianId = values.physicianId || undefined

      const result = await sendAndPreviewSurgeryReportMutation({
        surgeryReportTemplateId: implantReportInputs.reportType?.id as string,
        surgeryId,
        physicianId,
        attachments: mappedAttachments,
        inputVariables: values,
        preview: isPreview,
      } as any)

      if (isPreview) {
        // Handle preview response
        if (result.preview) {
          setPreviewPdfUrl(result.preview)
          setPreviewModalOpen(true)
        }
      } else {
        // Handle submit response
        if (result.message) {
          enqueueSnackbar(
            `${implantReportInputs?.['reportType']?.name} Report Sent Successfully`,
            { variant: 'success' }
          )
          setInputModalOpen(false)
          setReportAlreadySentModal(false)
        }
      }
    } catch (error) {
      console.error('Report error:', error)
      enqueueSnackbar(
        `Failed to ${
          isPreview ? 'generate preview' : 'send report'
        }. Please try again later.`,
        { variant: 'error' }
      )
      if (!isPreview) {
        setReportAlreadySentModal(false)
      }
    }
  }

  const handleFormSubmit = async (
    values: DynamicFormValues,
    isPreview: boolean = false
  ) => {
    if (
      !isPreview &&
      reportAlreadySent(
        implantReportInputs?.['reportType']
          ?.name as keyof typeof ImplantReportType
      ) &&
      !reportAlreadySentModal
    ) {
      setReportAlreadySentModal(true)
      return
    }

    await submitOrPreviewReport(values, isPreview)
  }

  const handleClosePreview = () => {
    setPreviewModalOpen(false)
    if (previewPdfUrl) {
      URL.revokeObjectURL(previewPdfUrl)
      setPreviewPdfUrl('')
    }
  }

  return {
    attachments,
    fileInputRef,
    reportAlreadySentModal,
    previewModalOpen,
    previewPdfUrl,
    handleAttach,
    handleOnClose,
    handleClosePreview,
    handleRemoveAttachment,
    setReportAlreadySentModal,
    handleAttachementFileChange,
    isLoadingSendAndPreview,
    handleFormSubmit,
    template,
  }
}
